import React, { useRef } from 'react'

import { FiUpload, FiLoader } from 'react-icons/fi'
import { useNavigate } from 'react-router-dom'

import { Alert, Modal, Button } from '@/components/ui'
import { useDashboard, useCompany } from '@/hooks'
import { CompanyDropdown } from '@/insights/components/CompanyDropdown'
import type { DashboardType, InsightConfigurationType } from '@/types/dashboard-types'

export interface ImportProps {
  onClose: () => void
  onSuccess: (slug: string) => void
}

const Import: React.FC<ImportProps> = ({ onClose, onSuccess }) => {
  const dashboardContext = useDashboard()
  const { companies } = useCompany()
  const navigate = useNavigate()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [working, setWorking] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)
  const [loading, setLoading] = React.useState<boolean>(false)
  const [validationErrors, setValidationErrors] = React.useState<string[]>([])
  const [successModalOpen, setSuccessModalOpen] = React.useState(false)
  const [importedDashboardTitle, setImportedDashboardTitle] = React.useState<string>('')
  const [importedDashboardSlug, setImportedDashboardSlug] = React.useState<string>('')
  const [pendingImport, setPendingImport] = React.useState<{
    dashboard: DashboardType
    insights: InsightConfigurationType[]
  } | null>(null)
  const [replacementCompanyId, setReplacementCompanyId] = React.useState<number | undefined>()

  const validateDashboardFormat = (data: unknown): data is DashboardType => {
    if (!data || typeof data !== 'object') {
      return false
    }

    const dashboard = data as Record<string, unknown>

    // Check required fields
    if (typeof dashboard.title !== 'string' || !dashboard.title.trim()) {
      return false
    }

    // Check insights array
    if (!Array.isArray(dashboard.insights)) {
      return false
    }

    // Validate each insight
    for (const insight of dashboard.insights) {
      if (!insight || typeof insight !== 'object') {
        return false
      }
      const insightObj = insight as Record<string, unknown>
      if (
        typeof insightObj.id !== 'string' ||
        typeof insightObj.slug !== 'string' ||
        typeof insightObj.title !== 'string'
      ) {
        return false
      }
    }

    return true
  }

  const validateCompanyAccess = (dashboard: DashboardType, insights: InsightConfigurationType[]): string[] => {
    const errors: string[] = []
    const accessibleCompanyIds = new Set(companies.map((c) => c.id))

    // Dashboard-wide params check
    const dashCompanyId = dashboard.parameters?.companyId
    if (dashCompanyId !== undefined && dashCompanyId !== null) {
      const id = typeof dashCompanyId === 'string' ? Number(dashCompanyId) : dashCompanyId
      if (typeof id === 'number' && (isNaN(id) || !accessibleCompanyIds.has(id))) {
        errors.push(`Dashboard-wide parameters reference company ID ${dashCompanyId} which you don't have access to.`)
      }
    }

    for (const insight of insights) {
      const companyId = insight.parameters?.companyId
      if (companyId !== undefined && companyId !== null) {
        // Only process if companyId is a number or string
        if (typeof companyId === 'number' || typeof companyId === 'string') {
          const companyIdNum = typeof companyId === 'string' ? Number(companyId) : companyId
          if (isNaN(companyIdNum) || !accessibleCompanyIds.has(companyIdNum)) {
            errors.push(`Insight "${insight.title}" references company ID ${companyId} which you don't have access to.`)
          }
        }
      }
    }

    return errors
  }

  const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file) {
      return
    }

    setWorking(true)
    setError(null)
    setValidationErrors([])

    try {
      const text = await file.text()
      let data: unknown

      try {
        data = JSON.parse(text)
      } catch {
        setError('Invalid JSON file. Please check the file format.')
        setWorking(false)
        return
      }

      // Validate dashboard format
      if (!validateDashboardFormat(data)) {
        setError('Invalid dashboard format. The file does not match the expected dashboard structure.')
        setWorking(false)
        return
      }

      const importedDashboard = data as DashboardType

      // Find unique dashboard name by appending (2), (3), etc. if needed
      const existingDashboards = dashboardContext.configuration.dashboards
      let uniqueDashboardTitle = importedDashboard.title
      let counter = 2
      while (existingDashboards.some((d) => d.title === uniqueDashboardTitle)) {
        uniqueDashboardTitle = `${importedDashboard.title} (${counter})`
        counter += 1
      }

      // Make insight IDs unique by appending -1, -2, etc. if needed
      const allExistingInsightIds = new Set<string>()
      existingDashboards.forEach((d) => {
        d.insights.forEach((insight) => {
          allExistingInsightIds.add(insight.id)
        })
      })

      const uniqueInsights = importedDashboard.insights.map((insight) => {
        let uniqueId = insight.id
        let idCounter = 1
        while (allExistingInsightIds.has(uniqueId)) {
          uniqueId = `${insight.id}-${idCounter}`
          idCounter += 1
        }
        allExistingInsightIds.add(uniqueId) // Track this ID for subsequent insights in the same import
        return {
          ...insight,
          id: uniqueId,
        }
      })

      // Validate company access for insights
      const companyAccessErrors = validateCompanyAccess(importedDashboard, uniqueInsights)
      if (companyAccessErrors.length > 0) {
        setPendingImport({ dashboard: { ...importedDashboard, title: uniqueDashboardTitle }, insights: uniqueInsights })
        setWorking(false)
        return
      }

      // Import the dashboard
      const dashboard = await dashboardContext.addDashboard({
        dashboard: {
          ...importedDashboard,
          title: uniqueDashboardTitle,
          slug: '', // Will be generated by addDashboard
          isDefault: false, // Never import as default
        },
        insights: uniqueInsights,
      })

      setWorking(false)
      setImportedDashboardTitle(uniqueDashboardTitle)
      setImportedDashboardSlug(dashboard.slug)
      setSuccessModalOpen(true)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred while importing the dashboard.')
      setWorking(false)
    } finally {
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = ''
      }
    }
  }

  const applyCompanyReplacement = (insights: InsightConfigurationType[], companyId: number) =>
    insights.map((insight) => ({
      ...insight,
      parameters: {
        ...insight.parameters,
        companyId,
        assetId: undefined,
        assetIds: undefined,
      },
    }))

  const handleConfirmWithCompany = async () => {
    if (!pendingImport || !replacementCompanyId) return
    setWorking(true)
    setLoading(true)
    const modifiedInsights = applyCompanyReplacement(pendingImport.insights, replacementCompanyId)
    const modifiedDashboard = {
      ...pendingImport.dashboard,
      slug: '',
      isDefault: false,
      parameters: {
        ...pendingImport.dashboard.parameters,
        ...(pendingImport.dashboard.parameters?.companyId
          ? { companyId: replacementCompanyId, assetId: undefined }
          : {}),
      },
    }
    const dashboard = await dashboardContext.addDashboard({
      dashboard: modifiedDashboard,
      insights: modifiedInsights,
    })
    setWorking(false)
    setImportedDashboardTitle(pendingImport.dashboard.title)
    setImportedDashboardSlug(dashboard.slug)
    setSuccessModalOpen(true)
    setPendingImport(null)
    setLoading(false)
  }

  const handleImportClick = () => {
    fileInputRef.current?.click()
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 p-6">
        <div className="space-y-4 mb-4">
          <h3 className="text-lg font-medium">Import Dashboard</h3>
          <p className="text-gray-600">
            Import a dashboard from a .cltrx file. The dashboard will be added to your dashboard list.
          </p>
        </div>

        {error && (
          <div className="mb-4">
            <Alert type="error" message={error} />
          </div>
        )}

        {validationErrors.length > 0 && (
          <div className="mb-4">
            <div className="bg-error/10 border border-error/30 rounded-md p-4">
              <p className="text-sm font-medium text-error mb-2">Validation Errors:</p>
              <ul className="text-sm text-error space-y-1">
                {validationErrors.map((err, idx) => (
                  <li key={idx}>• {err}</li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {pendingImport ? (
          <div className="space-y-4">
            <Alert
              type="warning"
              message="This dashboard references a company you don't have access to. Choose a replacement company to apply to all insights."
            />
            <CompanyDropdown value={replacementCompanyId} onChange={(id) => setReplacementCompanyId(id as number)} />
            <Button
              handleClick={handleConfirmWithCompany}
              isDisabled={!replacementCompanyId || working}
              className="btn btn-primary w-full"
            >
              {loading ? <FiLoader className="animate-spin" /> : 'Import with Selected Company'}
            </Button>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <input
              ref={fileInputRef}
              type="file"
              accept=".cltrx,.json,application/json"
              onChange={handleFileSelect}
              className="hidden"
            />
            <Button handleClick={handleImportClick} isDisabled={working} variation="primary">
              <FiUpload />
              {working ? 'Importing...' : 'Choose File'}
            </Button>
            <p className="text-xs text-gray-500 text-center">
              Select a .cltrx file exported from a dashboard to import it.
            </p>
          </div>
        )}
      </div>

      <div className="border-t border-base-300 p-4 flex gap-2 justify-end">
        <button onClick={onClose} className="btn btn-ghost" disabled={working}>
          Cancel
        </button>
      </div>

      <Modal
        title="Dashboard Imported Successfully"
        variation="success"
        show={successModalOpen}
        onHide={() => {
          setSuccessModalOpen(false)
          navigate(`/dashboards/${importedDashboardSlug}`)
          onSuccess(importedDashboardSlug)
          onClose()
        }}
        bodyContent={
          <div className="flex flex-col gap-4 mt-4">
            <p className="text-gray-600">Dashboard was imported as &quot;{importedDashboardTitle}&quot;.</p>
          </div>
        }
        footerButtons={[
          {
            label: 'OK',
            onClick: () => {
              setSuccessModalOpen(false)
              navigate(`/dashboards/${importedDashboardSlug}`)
              onSuccess(importedDashboardSlug)
              onClose()
            },
            class: 'btn-success text-white',
          },
        ]}
      />
    </div>
  )
}

export default Import
